import * as Sentry from '@sentry/react';
import type { ApolloError } from 'apollo-client';

import { useLocalizedContent } from 'bundles/internationalization-lib/hooks/useLocalizedContent';
import type { Locale } from 'bundles/shared-articles/types';
import type { SeoContentContentPageFieldsFragment } from 'bundles/unified-seo-content-common/__generated__/contentfulTypes';
import { DEFAULT_LOCALE_CODE, LOCALIZED_CONTENT_PAGE_QUERY_FIELDS } from 'bundles/unified-seo-content-common/constants';
import { SeoContentContentPageFieldsFragment as SeoContentContentPageFieldsFragmentDoc } from 'bundles/unified-seo-content-common/queries/SeoContentContentPageQuery';
import type { SEOContentPageType } from 'bundles/unified-seo-content-common/types';

type InnerProps = {
  slug: string;
  pageType: SEOContentPageType;
  preview?: boolean;
  subfolderLocaleCode?: Locale;
};

export type PageDataProps = {
  pageData?: SeoContentContentPageFieldsFragment;
  loading?: boolean;
  error?: ApolloError;
};

const useSeoContentContentPageQuery = ({
  slug,
  pageType,
  preview = false,
  subfolderLocaleCode = DEFAULT_LOCALE_CODE,
}: InnerProps): PageDataProps => {
  const { languageCode, countryCode } = subfolderLocaleCode;
  const { data, loading, error } = useLocalizedContent<SeoContentContentPageFieldsFragment>({
    isPreview: preview,
    contentType: LOCALIZED_CONTENT_PAGE_QUERY_FIELDS.contentType,
    fragmentDoc: SeoContentContentPageFieldsFragmentDoc,
    fragmentFieldsName: LOCALIZED_CONTENT_PAGE_QUERY_FIELDS.fragmentFieldsName,
    filters: { slug, pageType, countryCode, languageCode },
    countryCode,
    languageCode,
    onError: (errorInfo) =>
      Sentry.captureException(errorInfo, {
        extra: {
          message: 'Error fetching SEO content page data',
        },
      }),
  });

  return {
    pageData: data,
    loading,
    error,
  };
};

export default useSeoContentContentPageQuery;
